type TelemetryAttribute = string | number | boolean | null

type TelemetryAttributes = Readonly<Record<string, TelemetryAttribute>>

type TelemetryEvent = {
  readonly name: string
  readonly timestampMs: number
  readonly attributes?: TelemetryAttributes
  readonly durationMs?: number
  readonly outcome?: string
}

export type TelemetrySink = {
  emit(event: Readonly<TelemetryEvent>): void
}

type AsyncTelemetrySink = {
  emit(event: Readonly<TelemetryEvent>): void | PromiseLike<void>
}

type TelemetryEmitter = {
  emit(
    name: string,
    fields?: Omit<TelemetryEvent, 'name' | 'timestampMs'>
  ): boolean
}

type AsyncTelemetryEmitter = {
  emit(
    name: string,
    fields?: Omit<TelemetryEvent, 'name' | 'timestampMs'>
  ): Promise<boolean>
}

type TelemetryEmitterOptions = {
  readonly now?: () => number
  readonly baseAttributes?: TelemetryAttributes
  readonly onSinkError?: (
    error: unknown,
    event: Readonly<TelemetryEvent>
  ) => void
}

type AsyncTelemetryEmitterOptions = {
  readonly now?: () => number
  readonly baseAttributes?: TelemetryAttributes
  readonly onSinkError?: (
    error: unknown,
    event: Readonly<TelemetryEvent>
  ) => void | PromiseLike<void>
}

export function createTelemetryEmitter(
  sink: TelemetrySink,
  options: TelemetryEmitterOptions = {}
): TelemetryEmitter {
  const createEvent = createEventFactory(options)

  return {
    emit(
      name: string,
      fields: Omit<TelemetryEvent, 'name' | 'timestampMs'> = {}
    ): boolean {
      const event = createEvent(name, fields)

      try {
        const result: unknown = sink.emit(event)

        if (isThenable(result)) {
          observeThenable(result)
          reportSyncError(
            options,
            new TypeError(
              'A synchronous telemetry sink returned a Promise-like value. Use createAsyncTelemetryEmitter instead.'
            ),
            event
          )
          return false
        }

        return true
      } catch (error) {
        reportSyncError(options, error, event)
        return false
      }
    },
  }
}

export function createAsyncTelemetryEmitter(
  sink: AsyncTelemetrySink,
  options: AsyncTelemetryEmitterOptions = {}
): AsyncTelemetryEmitter {
  const createEvent = createEventFactory(options)

  return {
    async emit(
      name: string,
      fields: Omit<TelemetryEvent, 'name' | 'timestampMs'> = {}
    ): Promise<boolean> {
      const event = createEvent(name, fields)

      try {
        await sink.emit(event)
        return true
      } catch (error) {
        try {
          await options.onSinkError?.(error, event)
        } catch {
          // Telemetry must not change application control flow.
        }

        return false
      }
    },
  }
}

function createEventFactory(options: {
  readonly now?: () => number
  readonly baseAttributes?: TelemetryAttributes
}): (
  name: string,
  fields: Omit<TelemetryEvent, 'name' | 'timestampMs'>
) => Readonly<TelemetryEvent> {
  const now = options.now ?? Date.now
  const baseAttributes = freezeAttributes(options.baseAttributes)

  return (name, fields) => {
    assertEventName(name)
    assertDuration(fields.durationMs)

    const attributes = mergeAttributes(baseAttributes, fields.attributes)

    return Object.freeze({
      ...fields,
      ...(attributes === undefined ? {} : { attributes }),
      name,
      timestampMs: now(),
    })
  }
}

function reportSyncError(
  options: TelemetryEmitterOptions,
  error: unknown,
  event: Readonly<TelemetryEvent>
): void {
  try {
    const result: unknown = options.onSinkError?.(error, event)

    if (isThenable(result)) {
      observeThenable(result)
    }
  } catch {
    // Telemetry must not change application control flow.
  }
}

function assertDuration(durationMs: number | undefined): void {
  if (
    durationMs !== undefined &&
    (!Number.isFinite(durationMs) || durationMs < 0)
  ) {
    throw new RangeError(
      'Telemetry durationMs must be a finite non-negative number.'
    )
  }
}

function assertEventName(name: string): void {
  if (name.trim().length === 0) {
    throw new Error('Telemetry event name cannot be empty.')
  }
}

function freezeAttributes(
  attributes: TelemetryAttributes | undefined
): TelemetryAttributes | undefined {
  return attributes === undefined ? undefined : Object.freeze({ ...attributes })
}

function mergeAttributes(
  base: TelemetryAttributes | undefined,
  event: TelemetryAttributes | undefined
): TelemetryAttributes | undefined {
  if (base === undefined && event === undefined) {
    return undefined
  }

  return Object.freeze({ ...base, ...event })
}

function isThenable(value: unknown): value is PromiseLike<unknown> {
  if (
    (typeof value !== 'object' || value === null) &&
    typeof value !== 'function'
  ) {
    return false
  }

  return 'then' in value && typeof value.then === 'function'
}

function observeThenable(thenable: PromiseLike<unknown>): void {
  try {
    void Promise.resolve(thenable).catch(noop)
  } catch {
    // Accessing a hostile thenable may throw before a Promise exists.
  }
}

function noop(): void {}
